import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, ProviderAccreditationStatusService, Response, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { DeckAPI, FetchAPI, FormAPI, PanelAPI, Stamp, StampIcon, ToastNotificationAPI } from '@philips-emr/tws-components';
import { Color, ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnChange, OnDynamicContentRender, OnMount, OnSelectItem, OnUnselectItem } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import './accreditation.css';

import { AccreditationSidebarMenuAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

interface Menu {
  id: AccreditationMenuType,
  name: string,
  pendencies: Array<Pendencies>,
}

interface Pendencies {
  ieInformation: number,
  ieType: string,
  message: string,
  title: string,
}

enum AccreditationMenuType {
  ESTABLISHMENT = 1,
  ESTABLISHMENT_SPECIALTY = 2,
  SERVICES_AND_MATERIALS = 3,
  PACKAGES = 4,
  CLINICAL_STAFF = 5,
  PROFESSIONALS = 6,
  BEDS_AND_EQUIPMENTS = 7,
  QUALIFICATIONS = 8,
  COMPLEMENTARY_DATA = 9,
  DOCUMENTS = 10,
  BANK_DATA = 11,
  CONCLUSION = 12
}

@Controller({ fragment: FragmentNames.ACCREDITATION_SIDEBAR_MENU })
class AccreditationSidebarMenuController extends BaseController {
  @Inject(Aliases.PANEL_ACCREDITATION_SPECIALTY)
  private panelSpecialty: PanelAPI;
  @Inject(Aliases.PANEL_SERVICES_AND_MATERIALS)
  private panelServicesAndMaterials: PanelAPI;
  @Inject(Aliases.PANEL_PACKAGES)
  private panelPackages: PanelAPI;
  @Inject(Aliases.PANEL_BEDS_AND_EQUIPMENTS)
  private panelBedsAndEquipments: PanelAPI;
  @Inject(Aliases.PANEL_CLINICAL_STAFF)
  private panelClinicalStaff: PanelAPI;
  @Inject(Aliases.PANEL_DOCUMENTS)
  private panelDocuments: PanelAPI;
  @Inject(Aliases.PANEL_COMPLEMENTARY_DATA)
  private panelComplementaryData: PanelAPI;
  @Inject(Aliases.PANEL_PROFESSIONALS)
  private panelProfessionals: PanelAPI;
  @Inject(Aliases.PANEL_QUALIFICATIONS)
  private panelQualifications: PanelAPI;
  @Inject(Aliases.PANEL_CONCLUSION)
  private panelConclusion: PanelAPI;
  @Inject(Aliases.DECK_MENU)
  private deckMenu: DeckAPI;
  @Inject(Aliases.FORM_MENU_MOBILE)
  private formMenuMobile: FormAPI;
  @Inject(Aliases.PANEL_REFUSAL_TOAST)
  private panelToast: PanelAPI;
  @Inject(Aliases.PANEL_ESTABLISHMENT)
  private panelEstablishment: PanelAPI;
  @Inject(Aliases.PANEL_ACCREDITATION_BANK_DATA)
  private panelBankData: PanelAPI;

  private static DENIED = '6';
  private AWAITING_CNES = '7';

  @OnAfterFetch(Aliases.FETCH_MENUS_MOBILE)
  onAfterFetch(event: FetchEvent): void {
    const data = event.data as [{ pendencies: [], hideImage: boolean }];
    if (data) {
      data.forEach(item => item.hideImage = isEmptyArrayOrObject(item.pendencies));
      const record = [...data].shift();
      record && this.formMenuMobile.setRecord({ menuMobile: record });
      const { id } = record as unknown as Menu;
      this.visibilityControl(id);
    }
  }

  @OnMount([Aliases.FETCH_MENUS, Aliases.FETCH_MENUS_MOBILE])
  onMountMenu(event: MountEvent): void {
    const fetch = event.target as FetchAPI;
    const { nrSeqAccreditation, nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    fetch.setParameters({ nrSeqAccreditation, nrSeqTypeAccreditation });
    fetch.forceFetch();
  }

  @OnMount(Aliases.TOAST_NOTIFICATION_REFUSAL)
  onMountToastRefusal(event: MountEvent): void {
    const api = event.target as ToastNotificationAPI;
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();

    ProviderAccreditationStatusService.getAccreditationStatus(Number(nrSeqAccreditation), Number(nrSeqProvider)).then((response) => {
      const { status } = response.body as unknown as Response;
      this.panelToast.setVisible(status == AccreditationSidebarMenuController.DENIED);

      if (status == AccreditationSidebarMenuController.DENIED) {
        HttpService.get<any>(Endpoint.GET.ACCREDITATION, { nrSeqProvider, nrSeqAccreditation }).then(resp => {
          const body = resp.body;
          if (body) {
            const { refusalReason } = body;
            api.setMessage(refusalReason);
          }
        }).catch(() => ToastService.open({ level: ToastLevel.ERROR, message: MessageService.translate('provider.general-error') }));
      }
    }).catch(() => ToastService.open({ level: ToastLevel.ERROR, message: MessageService.translate('provider.general-error') }));
  }

  @OnDynamicContentRender(Aliases.HPMS_CUSTOM_CARD)
  onDynamicContentRenderField(event: RenderEvent): void {
    const { render } = event;
    const { pendencies } = event.data as unknown as Menu;

    if (!isEmptyArrayOrObject(pendencies)) {
      render((): ReactElement => <Stamp icon={StampIcon.WARNING} color={Color.SAFETY_ORANGE} label="" />);
    }
  }

  @OnAfterFetch(Aliases.FETCH_MENUS)
  onAfterFetchMenu(event: FetchEvent): void {
    if (event.data) {
      const data = event.data as Menu[];
      const id = data[0].id;
      this.deckMenu.selectCard(String(id));
      this.visibilityControl(id);
    }
  }

  @OnSelectItem(Aliases.DECK_MENU)
  onselect(event: SelectItemEvent<Menu>): void {
    const { id } = event.item;
    this.visibilityControl(id);
  }

  @OnUnselectItem(Aliases.DECK_MENU)
  onUnselect(event: SelectItemEvent<Menu>): void {
    const { id } = event.item;
    this.visibilityControl(id);
  }

  @OnChange(Aliases.AUTOCOMPLETE_MENU_MOBILE)
  onChange(event: ChangeEvent<Menu>): void {
    this.visibilityControl(event.value.id);
  }

  @OnMount(Aliases.PANEL_ACCREDITATION)
  onMountAccreditation(event: MountEvent): void {
    const api = event.target as PanelAPI;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;

    ProviderAccreditationStatusService.getAccreditationStatus(nrSeqAccreditation as number, nrSeqProvider).then((response) => {

      if (response.body) {
        const { status } = response.body;
        if (status != '7') {
          api.setVisible(true);
        }
      }
    }).catch(() => ToastService.open({ message: MessageService.translate('provider.error-process-request'), level: ToastLevel.ERROR }));
  }

  @OnMount(Aliases.PANEL_CNES)
  onMountCnes(event: MountEvent): void {
    const api = event.target as PanelAPI;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;

    ProviderAccreditationStatusService.getAccreditationStatus(nrSeqAccreditation as number, nrSeqProvider).then((response) => {
      if (response.body) {
        const { status } = response.body;
        if (status == this.AWAITING_CNES) {
          api.setVisible(true);
        }
      }
    }).catch(() => ToastService.open({ message: MessageService.translate('provider.error-process-request'), level: ToastLevel.ERROR }));
  }

  private visibilityControl(menu: AccreditationMenuType): void {
    this.panelEstablishment.setVisible(menu == AccreditationMenuType.ESTABLISHMENT);
    this.panelSpecialty.setVisible(menu === AccreditationMenuType.ESTABLISHMENT_SPECIALTY);
    this.panelServicesAndMaterials.setVisible(menu === AccreditationMenuType.SERVICES_AND_MATERIALS);
    this.panelPackages.setVisible(menu === AccreditationMenuType.PACKAGES);
    this.panelClinicalStaff.setVisible(menu === AccreditationMenuType.CLINICAL_STAFF);
    this.panelBedsAndEquipments.setVisible(menu === AccreditationMenuType.BEDS_AND_EQUIPMENTS);
    this.panelDocuments.setVisible(menu === AccreditationMenuType.DOCUMENTS);
    this.panelComplementaryData.setVisible(menu === AccreditationMenuType.COMPLEMENTARY_DATA);
    this.panelProfessionals.setVisible(menu === AccreditationMenuType.PROFESSIONALS);
    this.panelQualifications.setVisible(menu === AccreditationMenuType.QUALIFICATIONS);
    this.panelBankData.setVisible(menu === AccreditationMenuType.BANK_DATA);
    this.panelConclusion.setVisible(menu === AccreditationMenuType.CONCLUSION);
  }
}

export {
  AccreditationSidebarMenuController,
};
